import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isFuture, isPast } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Gift } from '#src/atoms/icons/Gift.tsx';
import { Timer } from '#src/atoms/icons/Timer.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyS } from '#src/atoms/typography/index.tsx';
import { LiveDot } from '#src/components/live/LiveDot/index.tsx';
import { TimeLeft } from '#src/contexts/ticker/TimeLeft/index.tsx';
import { navLabels } from '#src/lib/glossary.ts';
import { FixtureState } from '#src/lib/so5.ts';

import type { LeaderboardStatus_so5Leaderboard } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ gap: 0.5 })`
  padding: var(--half-unit) var(--unit);
  border-radius: var(--unit);
  backdrop-filter: blur(2px);
  white-space: nowrap;

  &.opened {
    color: var(--c-yellow-400);
    background: rgba(240, 201, 86, 0.16);
  }
  &.live {
    background: rgba(245, 103, 87, 0.16);
    &.onLightBackground {
      background: var(--c-red-400);
    }
  }
  &.preparing {
    color: var(--c-blue-300);
    background: rgba(var(--c-rgb-blue-300), 0.3);
  }
  &.waitingForRewards {
    background: rgba(61, 220, 132, 0.16);
    color: var(--c-onBase-success);
  }
`;

type CommonProps = {
  onLightBackground?: boolean;
  so5Leaderboard?: LeaderboardStatus_so5Leaderboard;
};

type Props = CommonProps &
  (
    | {
        fixtureState: FixtureState.started;
      }
    | {
        fixtureState: FixtureState.closed;
        expectedRewardsDistributionDate?: Nullable<Date>;
      }
    | {
        fixtureState: FixtureState.opened;
        startDate: Date;
      }
  );

export const FixtureStatus = (props: Props) => {
  const { fixtureState, onLightBackground, so5Leaderboard } = props;

  if (
    so5Leaderboard &&
    (!isPast(so5Leaderboard.cutOffDate) || fixtureState === FixtureState.opened)
  ) {
    return (
      <Root className="opened">
        <Timer size={2} />
        <BodyS as="p" bold color="var(--c-white)">
          <TimeLeft time={so5Leaderboard.cutOffDate} />
        </BodyS>
      </Root>
    );
  }
  if (fixtureState === FixtureState.started) {
    const textColor = onLightBackground ? 'var(--c-white)' : 'var(--c-red-400)';
    return (
      <Root
        className={classNames('live', {
          onLightBackground,
        })}
      >
        <LiveDot animate color={textColor} />
        <BodyS as="p" bold color={textColor}>
          <FormattedMessage {...navLabels.live} />
        </BodyS>
      </Root>
    );
  }
  if (fixtureState === FixtureState.closed) {
    const { expectedRewardsDistributionDate } = props;
    if (
      expectedRewardsDistributionDate &&
      isFuture(expectedRewardsDistributionDate)
    ) {
      return (
        <Root className="waitingForRewards">
          <Gift size={1.5} />
          <BodyS as="p" bold color="var(--c-white)">
            <TimeLeft time={expectedRewardsDistributionDate} />
          </BodyS>
        </Root>
      );
    }
  }

  return null;
};

FixtureStatus.fragments = {
  so5Leaderboard: gql`
    fragment LeaderboardStatus_so5Leaderboard on So5Leaderboard {
      slug
      startDate
      cutOffDate
    }
  ` as TypedDocumentNode<LeaderboardStatus_so5Leaderboard>,
};
